/** @jsx jsx */
import { ButtonComponentProps, ChevronDownIcon } from "@eworkit/iris";
import React, { ReactElement, ReactNode, useCallback, useMemo } from "react";
import { SealedInitialState } from "reakit-utils/useSealedState";
import { unstable_useId as useId } from "reakit/Id";
import { MenuButtonProps, useMenuState } from "reakit/Menu";
import { MenuInitialState } from "reakit/Menu";
import { jsx } from "theme-ui";

import { throttle } from "@/utils/throttle";

import { Menu, MenuButton } from "../Menu";
import { NavButton } from "../NavItems";
import { navigationMenuPadding, nestedMenuAnimationStyles, nestedMenuStyles } from "./../config";

export interface NestedMenuProps extends ButtonComponentProps, MenuButtonProps {
  buttonContent: ReactNode;
  ariaLabel?: string;
  initialMenuState?: SealedInitialState<MenuInitialState>;
  level?: number;
}

type PropsWithMenuChildren<P> = P & { children: ReactElement | ReactElement[] };

const initialLevelStates: Array<SealedInitialState<MenuInitialState>> = [
  {},
  { gutter: 24 },
  { unstable_offset: [-navigationMenuPadding, navigationMenuPadding + 8] },
];

export const NestedMenu = React.forwardRef((props: PropsWithMenuChildren<NestedMenuProps>, ref) => {
  const { level = 0, initialMenuState, buttonContent, ariaLabel, ...restProps } = props;
  const nestedMenuData = useMenuState({
    animated: true,
    ...initialLevelStates[level],
    ...initialMenuState,
  });
  const children: any[] = React.Children.toArray(props.children);

  const handleVisibility = useCallback(
    (visible: boolean | undefined) => {
      if (visible === undefined) {
        nestedMenuData.toggle();
        return;
      }
      nestedMenuData.setVisible(visible);
    },
    [nestedMenuData],
  );

  const throttledSetOpen = useMemo(() => throttle(handleVisibility, 160, { leading: false }), [handleVisibility]);

  const showMenu = useCallback(() => {
    throttledSetOpen(true);
  }, [throttledSetOpen]);

  const hideMenu = useCallback(() => {
    throttledSetOpen(false);
  }, [throttledSetOpen]);

  const toggleMenu = useCallback(() => {
    throttledSetOpen();
  }, [throttledSetOpen]);

  const { id = "" } = useId();

  return (
    <div onMouseEnter={showMenu} onMouseLeave={hideMenu}>
      <MenuButton
        as={NavButton}
        ref={ref}
        {...nestedMenuData}
        {...restProps}
        id={id}
        aria-label={ariaLabel}
        iconRight={<ChevronDownIcon />}
        show={showMenu}
        hide={hideMenu}
        toggle={toggleMenu}
        active={nestedMenuData.visible}>
        {buttonContent}
      </MenuButton>
      {nestedMenuData.visible && (
        <Menu
          {...nestedMenuData}
          aria-labelledby={id}
          hide={hideMenu}
          sx={{ ...nestedMenuAnimationStyles, ...nestedMenuStyles }}>
          {children.map(child => React.cloneElement(child, { ...nestedMenuData }))}
        </Menu>
      )}
    </div>
  );
});
